package com.veldekar.components.log

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.TextDecorationLine.Companion.None
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.ariaHidden
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.objectFit
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.textDecorationLine
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import com.veldekar.components.widgets.LazyImg
import com.veldekar.components.widgets.LocalizedSessionDate
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.keywords.auto
import org.jetbrains.compose.web.dom.Div

/**
 * SimpleSessionListItem renders a compact session list item with only a date link.
 * Used for sessions without title or description.
 */
@Composable
fun SimpleSessionListItem(path: String, date: LocalDate, veldekarDates: List<String> = emptyList()) {
    Link(path = path, modifier = Modifier.fillMaxWidth().padding(0.75.cssRem)) {
        veldekarDates.forEach { id ->
            Div(Modifier.id(id).toAttrs())
        }
        LocalizedSessionDate(date)
    }
}

/**
 * SessionListItem renders a single session list item with optional image and description.
 */
@Composable
fun SessionListItem(
    path: String,
    title: String,
    description: String,
    imageUrl: String,
    imageWidth: Int? = null,
    imageHeight: Int? = null,
    date: LocalDate,
    veldekarDates: List<String> = emptyList(),
    isAiText: Boolean = false,
    isAiImage: Boolean = false,
    aiHint: String? = null,
    eagerImage: Boolean = false,
) {
    val palette = ColorMode.current.toPalette()
    Div(Modifier.fillMaxWidth().toAttrs()) {
        veldekarDates.forEach { id ->
            Div(Modifier.id(id).toAttrs())
        }
        Link(
            path = path,
            modifier = Modifier.fillMaxWidth().textDecorationLine(None),
        ) {
            Div(
                SessionListItemContainerStyle.toModifier().toAttrs {
                    if (aiHint != null) attr("data-ai-hint", aiHint)
                }
            ) {
                if (aiHint != null) {
                    SpanText(
                        "AI Note: $aiHint",
                        modifier = Modifier.styleModifier {
                            property("position", "absolute")
                            property("width", "1px")
                            property("height", "1px")
                            property("overflow", "hidden")
                            property("clip", "rect(0 0 0 0)")
                            property("white-space", "nowrap")
                        }
                    )
                }
                if (imageUrl.isNotEmpty()) {
                    val imageAlt = if (title.isNotEmpty() || description.isNotEmpty()) {
                        ""
                    } else {
                        "Session from $date"
                    }
                    if (isAiImage) {
                        Box(SessionListItemImageStyle.toModifier(), contentAlignment = Alignment.BottomEnd) {
                            LazyImg(
                                src = imageUrl,
                                alt = imageAlt,
                                width = imageWidth,
                                height = imageHeight,
                                eager = eagerImage,
                                modifier = Modifier.fillMaxWidth().height(auto).objectFit(ObjectFit.Cover)
                            )
                            SpanText(
                                "✨",
                                modifier = Modifier
                                    .ariaHidden(true)
                                    .fontSize(2.4.cssRem)
                                    .margin(bottom = (-.65).cssRem, right = (-0).cssRem)
                                    .styleModifier {
                                        property("text-shadow", "0 0 2px black, 0 0 2px black, 0 0 2px black, 0 0 2px black")
                                    }
                            )
                        }
                    } else {
                        LazyImg(
                            src = imageUrl,
                            alt = imageAlt,
                            width = imageWidth,
                            height = imageHeight,
                            eager = eagerImage,
                            modifier = SessionListItemImageStyle.toModifier()
                        )
                    }
                }
                Div(SessionListItemTextColumnStyle.toModifier().toAttrs()) {
                    LocalizedSessionDate(date, modifier = Modifier.classNames("session-date-link"))
                    if (title.isNotEmpty()) {
                        Div {
                            if (isAiText) {
                                SpanText(
                                    "✨ ",
                                    modifier = Modifier
                                        .ariaHidden(true)
                                        .styleModifier {
                                            property(
                                                "text-shadow",
                                                "0 0 2px black, 0 0 2px black, 0 0 2px black, 0 0 2px black"
                                            )
                                        }
                                )
                            }
                            SpanText(
                                title,
                                modifier = Modifier
                                    .fontSize(1.1.cssRem)
                                    .fontWeight(700)
                                    .color(palette.color)
                                    .textDecorationLine(None)
                            )
                        }
                    }
                    if (description.isNotEmpty()) {
                        Div {
                            if (isAiText) {
                                SpanText(
                                    "✨ ",
                                    modifier = Modifier
                                        .ariaHidden(true)
                                        .styleModifier {
                                            property(
                                                "text-shadow",
                                                "0 0 2px black, 0 0 2px black, 0 0 2px black, 0 0 2px black"
                                            )
                                        }
                                )
                            }
                            SpanText(
                                description,
                                modifier = Modifier
                                    .fontSize(0.9.cssRem)
                                    .color(palette.color)
                                    .textDecorationLine(None)
                            )
                        }
                    }
                }
            }
        }
    }
}
